// ============================================
// 📞 LOG CALL SHEET — shared by the View window, the Leads table and the phone cards
// src/components/LogCallSheet.jsx
// Same rules as "Log Call Attempt" and the call queue (POST /api/leads/:id/log-attempt)
// ============================================
import React, { useState, useEffect } from 'react';
import { X, Phone, Check, XCircle, PhoneOff, Clock, ChevronLeft, RefreshCw } from './icons/HeroshhiIcons';
import config from '../config';

// ============================================
// 📞 LOG CALL (same rules as "Log Call Attempt" and the call queue)
// ============================================
export const firstName = (n) => String(n || 'them').split(' ')[0];
const quickTimes = () => {
  const now = new Date(), out = [];
  const ev = new Date(now); ev.setHours(18, 0, 0, 0);
  if (now.getHours() < 17) out.push({ label: 'This evening', sub: '6:00 PM', at: ev });
  const t = new Date(now); t.setDate(t.getDate() + 1); t.setHours(11, 0, 0, 0);
  out.push({ label: 'Tomorrow', sub: '11:00 AM', at: t });
  const t2 = new Date(t); t2.setHours(17, 0, 0, 0);
  out.push({ label: 'Tomorrow', sub: '5:00 PM', at: t2 });
  const d2 = new Date(now); d2.setDate(d2.getDate() + 2); d2.setHours(11, 0, 0, 0);
  out.push({ label: d2.toLocaleDateString('en-IN', { weekday: 'long' }), sub: '11:00 AM', at: d2 });
  return out.slice(0, 3);
};
const DROP_REASONS = ['Price', 'Too far', 'Just browsing', 'Booked elsewhere', 'Wrong number', 'Other'];

export default function LogCallSheet({ lead, onClose, onSaved, callSeconds }) {
  // ⚡ Escape closes this window
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  const [step, setStep] = useState('outcome');   // outcome | pick | reason
  const [pickFor, setPickFor] = useState('busy');
  const [custom, setCustom] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const digits = String(lead.phone || '').replace(/\D/g, '');
  const tel = digits ? `tel:+${digits.length === 10 ? '91' + digits : digits}` : null;

  const save = async (body) => {
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${config.API_URL}/api/leads/${lead._id}/log-attempt`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, notes: note.trim() || undefined, ...(Number.isFinite(callSeconds) ? { callSeconds } : {}) })
      }).then(x => x.json());
      if (!r.success) throw new Error(r.message || 'Could not save');
      onSaved && onSaved(); onClose();
    } catch (e) { setErr(e.message || 'Could not save. Try again.'); }
    finally { setSaving(false); }
  };
  const opt = (onClick, Icon, title, sub, dark) => (
    <button type="button" onClick={onClick} disabled={saving}
      className={`w-full min-h-[60px] px-4 rounded-xl flex items-center gap-3 text-left disabled:opacity-60 ${dark ? 'bg-[#10203A] text-white' : 'bg-gray-50 border border-gray-200 text-[#10203A]'}`}>
      <span className={`w-9 h-9 flex-none rounded-lg flex items-center justify-center ${dark ? 'bg-[#F2B12A] text-[#10203A]' : 'bg-white border border-gray-200'}`}><Icon size={18} /></span>
      <span><span className="block font-extrabold">{title}</span><span className={`block text-xs ${dark ? 'text-slate-300' : 'text-gray-600'}`}>{sub}</span></span>
    </button>
  );
  const at = (d) => (pickFor === 'interested'
    ? save({ outcome: 'connected', result: 'interested', followUpDate: d.toISOString() })
    : save({ outcome: 'busy', followUpDate: d.toISOString() }));

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-end md:items-center justify-center" onClick={() => !saving && onClose()}>
      <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-4 space-y-2.5" style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }} onClick={e => e.stopPropagation()}>
        {step === 'outcome' && (<>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-extrabold text-[#10203A]">How did the call with {firstName(lead.name)} go?</h3>
            <button type="button" onClick={onClose} aria-label="Close" className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"><X size={18} /></button>
          </div>
          {tel && <a href={tel} className="md:hidden flex items-center justify-center gap-2 min-h-[48px] rounded-xl bg-[#F2B12A] text-[#10203A] font-extrabold"><Phone size={18} /> Call {lead.phone}</a>}
          {opt(() => { setPickFor('interested'); setStep('pick'); }, Check, 'Interested', 'Connected. Next: pick the next step', true)}
          {opt(() => setStep('reason'), XCircle, 'Not interested', 'Connected. Then one tap for the reason')}
          {opt(() => save({ outcome: 'no_answer' }), PhoneOff, 'No answer', 'Back in the queue later, within working hours')}
          {opt(() => { setPickFor('busy'); setStep('pick'); }, Clock, 'Busy / call back later', 'Pick when to call again')}
        </>)}
        {step === 'pick' && (<>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setStep('outcome')} aria-label="Back" className="min-h-[40px] min-w-[40px] -ml-2 flex items-center justify-center text-[#10203A]"><ChevronLeft size={20} /></button>
            <h3 className="text-lg font-extrabold text-[#10203A]">{pickFor === 'interested' ? 'Next step: when?' : `Call ${firstName(lead.name)} back when?`}</h3>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {quickTimes().map((t, i) => (
              <button key={i} type="button" disabled={saving} onClick={() => at(t.at)} className="min-h-[56px] rounded-xl bg-gray-50 border border-gray-200 text-left px-3 disabled:opacity-60">
                <span className="block font-extrabold text-[#10203A]">{t.label}</span><span className="block text-xs text-gray-600">{t.sub}</span>
              </button>
            ))}
            <label className="min-h-[56px] rounded-xl bg-gray-50 border border-gray-200 px-3 py-1.5 flex flex-col justify-center">
              <span className="font-extrabold text-[#10203A]">Pick a time</span>
              <input type="datetime-local" value={custom} onChange={e => setCustom(e.target.value)} className="text-xs bg-transparent border-0 p-0" />
            </label>
          </div>
          <label className="block text-xs font-bold text-gray-600">Note (optional)
            <input value={note} onChange={e => setNote(e.target.value)} className="mt-1 w-full min-h-[44px] rounded-lg border border-gray-300 px-3 text-sm font-normal" />
          </label>
          {custom && <button type="button" disabled={saving} onClick={() => at(new Date(custom))} className="w-full min-h-[48px] rounded-xl bg-[#10203A] text-white font-extrabold disabled:opacity-60">Save</button>}
        </>)}
        {step === 'reason' && (<>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setStep('outcome')} aria-label="Back" className="min-h-[40px] min-w-[40px] -ml-2 flex items-center justify-center text-[#10203A]"><ChevronLeft size={20} /></button>
            <h3 className="text-lg font-extrabold text-[#10203A]">Why not interested?</h3>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {DROP_REASONS.map(r => (
              <button key={r} type="button" disabled={saving} onClick={() => save({ outcome: 'connected', result: 'not_interested', dropReason: r })}
                className="min-h-[48px] rounded-xl bg-gray-50 border border-gray-200 text-sm font-bold text-[#10203A] disabled:opacity-60">{r}</button>
            ))}
          </div>
        </>)}
        {err && <p className="text-sm text-red-700">{err}</p>}
        {saving && <p className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Saving…</p>}
      </div>
    </div>
  );
}